import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CreditCard, Plus, Trash2, Save, Loader2, Banknote } from "lucide-react";

const slug = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || `metode_${Date.now()}`;

export default function PaymentMethods() {
  const [methods, setMethods] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/payment-methods").then((r) => setMethods(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const setM = (i, k, v) => setMethods((p) => p.map((m, x) => (x === i ? { ...m, [k]: v } : m)));
  const addM = () => setMethods((p) => [...p, { id: `metode_${Date.now()}`, label: "", fee_percent: 0, is_cash: false, active: true }]);
  const delM = (i) => setMethods((p) => p.filter((_, x) => x !== i));

  const save = async () => {
    const clean = methods
      .filter((m) => (m.label || "").trim())
      .map((m) => ({ id: m.id || slug(m.label), label: m.label.trim(), fee_percent: Number(m.fee_percent || 0), is_cash: !!m.is_cash, active: m.active !== false }));
    if (!clean.some((m) => m.is_cash)) { toast.error("Harus ada minimal 1 metode Tunai (centang 'Tunai')"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/payment-methods", { methods: clean });
      setMethods(data);
      toast.success("Metode pembayaran disimpan");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="Metode Pembayaran" subtitle="Kelola jenis pembayaran & biaya persen (mis. Kartu Kredit 2%)" />
      <div className="flex-1 overflow-y-auto p-5">
        <div className="max-w-3xl space-y-3">
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="payment-methods-table">
              <thead className="bg-[#F7F9FB] text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left p-3">Nama Metode</th>
                  <th className="text-center p-3 w-28">Biaya %</th>
                  <th className="text-center p-3 w-24">Tunai</th>
                  <th className="text-center p-3 w-24">Aktif</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {methods.map((m, i) => (
                  <tr key={i} data-testid={`method-row-${i}`} className="border-t border-slate-200">
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        {m.is_cash ? <Banknote className="h-4 w-4 text-emerald-600 shrink-0" /> : <CreditCard className="h-4 w-4 text-slate-500 shrink-0" />}
                        <Input data-testid={`method-label-${i}`} value={m.label} onChange={(e) => setM(i, "label", e.target.value)} placeholder="mis. Kartu Kredit" className="h-9 bg-[#F7F9FB] border-slate-300" />
                      </div>
                    </td>
                    <td className="p-2">
                      <Input data-testid={`method-fee-${i}`} type="number" step="0.1" value={m.fee_percent} onChange={(e) => setM(i, "fee_percent", e.target.value)} disabled={m.is_cash} className="h-9 text-right font-mono bg-[#F7F9FB] border-slate-300 disabled:opacity-40" />
                    </td>
                    <td className="p-2 text-center">
                      <input data-testid={`method-cash-${i}`} type="checkbox" checked={!!m.is_cash} onChange={(e) => setM(i, "is_cash", e.target.checked)} className="h-4 w-4 accent-emerald-500" />
                    </td>
                    <td className="p-2 text-center">
                      <input data-testid={`method-active-${i}`} type="checkbox" checked={m.active !== false} onChange={(e) => setM(i, "active", e.target.checked)} className="h-4 w-4 accent-emerald-500" />
                    </td>
                    <td className="p-2 text-center">
                      <button data-testid={`method-del-${i}`} onClick={() => delM(i)} className="text-slate-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="add-method-btn" variant="outline" onClick={addM} className="border-slate-300 bg-transparent hover:bg-[#F7F9FB] gap-2">
              <Plus className="h-4 w-4" /> Tambah Metode
            </Button>
            <Button data-testid="save-methods-btn" onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold gap-2 ml-auto">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan
            </Button>
          </div>
          <p className="text-xs text-slate-500">Biaya % akan ditambahkan ke total yang dibayar pelanggan saat metode itu dipakai (mis. Kartu Kredit 2%). Metode "Tunai" tidak dikenai biaya dan dipakai untuk hitung kas laci.</p>
        </div>
      </div>
    </>
  );
}
